// Salary Calculation logic - runs in the browser (not the backend).
//
//   Total Days in Month = calendar days in the given Month (current year)
//   Working Days        = Total Attendance - Leave
//   Pay Salary           = Gross Salary x (Total Attendance / Total Days in Month), rounded UP to the next whole rupee (e.g. 9.33 -> 10)
//                          - unless row.consider_30_days is true, in which
//                          case the divisor is always a flat 30 instead of
//                          the real days in month, so this employee's
//                          effective daily rate stays stable every month
//                          (e.g. Gross Salary 12000 always means ₹400/day,
//                          not ₹400 one month and ₹387 the next purely
//                          because of month length).
//   Pay Salary (with OT) = Pay Salary + OT Amount, rounded
//   Net Salary           = Pay Salary (with OT) - Advance, rounded
//
// Employees in the "PF" Employee Group (row.group) use a different
// formula entirely - OT isn't part of their payout, and there are additional
// statutory deductions. Their "Net Salary" becomes Total Payable Salary
// instead, so everything downstream (Confirm & Save Payment, Bank Letter,
// Excel export) keeps working unchanged, just reading row.netSalary as usual:
//   Basic (50%)          = Pay Salary x 0.5, rounded
//   PF (12%)              = Basic x 0.12, rounded
//   ESI (0.75%)           = Pay Salary x 0.0075, rounded
//   Total Deduction       = PF + ESI
//   Total Payable Salary  = Pay Salary - Advance - Total Deduction

import { PF_ENABLED_GROUP_NAME } from '../config/groupingConfig'

export const MONTH_NUMBERS = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
}

// Returns the number of days in the given month name for the given year,
// or null if the month name isn't recognized (e.g. a typo in the Excel file).
export function getDaysInMonth(monthName, year) {
  const monthNumber = MONTH_NUMBERS[String(monthName).trim().toLowerCase()]
  if (!monthNumber) {
    return null
  }
  // Day 0 of "next month" is the last day of the target month.
  return new Date(year, monthNumber, 0).getDate()
}

export function calculateRow(row, year) {
  const totalAttendance = Number(row.total_attendance)
  const leave = Number(row.leave)
  const overtimeAmount = Number(row.overtime_amount)
  const advanceDeduction = Number(row.advance_deduction)
  const grossSalary = Number(row.gross_salary)
  // daysInMonth stays the real calendar count (still shown as-is in the
  // "Days in Month" column); payDivisor is what Pay Salary actually divides
  // by - a flat 30 instead, when this employee's consider_30_days is on, so
  // their effective daily rate stays stable every month instead of
  // drifting between 28/30/31-day months.
  const daysInMonth = getDaysInMonth(row.month, year)
  const payDivisor = row.consider_30_days ? 30 : daysInMonth

  const workingDays = totalAttendance - leave
  // Ceiling, not standard rounding - per explicit instruction, the employee
  // should never lose a fraction of a rupee to rounding (e.g. 9.33 -> 10).
  // Rounded to paisa (2 decimals) before ceiling to strip binary floating-point
  // noise (e.g. 12000 * (16.5/30) evaluates to 6600.000000000001 in JS, which
  // Math.ceil would otherwise bump to 6601 even though the true value is 6600).
  const rawPaySalary = payDivisor ? grossSalary * (totalAttendance / payDivisor) : null
  const paySalary = rawPaySalary === null ? null : Math.ceil(Math.round(rawPaySalary * 100) / 100)

  const basicSalary = paySalary === null ? null : Math.round(paySalary * 0.5)
  const pf = basicSalary === null ? null : Math.round(basicSalary * 0.12)
  const esi = paySalary === null ? null : Math.round(paySalary * 0.0075)
  const totalDeduction = pf === null || esi === null ? null : pf + esi
  const totalPayableSalary =
    paySalary === null || totalDeduction === null ? null : paySalary - advanceDeduction - totalDeduction

  const paySalaryWithOt = paySalary === null ? null : Math.round(paySalary + overtimeAmount)
  const regularNetSalary = paySalaryWithOt === null ? null : Math.round(paySalaryWithOt - advanceDeduction)

  const isPfEnabled = row.group === PF_ENABLED_GROUP_NAME

  return {
    ...row,
    daysInMonth,
    workingDays,
    paySalary,
    basicSalary,
    pf,
    esi,
    totalDeduction,
    totalPayableSalary,
    // PF employees: OT doesn't apply, and Total Payable Salary
    // becomes their "Net Salary" so Confirm & Save/Bank Letter/Excel export
    // all keep working unchanged for them.
    paySalaryWithOt: isPfEnabled ? paySalary : paySalaryWithOt,
    netSalary: isPfEnabled ? totalPayableSalary : regularNetSalary,
  }
}

export function calculateRows(rows, year) {
  return rows.map((row) => calculateRow(row, year))
}
